import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronDown, ClipboardCheck, ShieldCheck, Smartphone, Users } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/marketing/Reveal";
import { Label, cta, ctaPrimary, ctaSecondary } from "@/components/marketing/MarketingPrimitives";
import { DEMO_PATH } from "@/components/marketing/PublicHeader";

export { Label };

const FEATURES = [
  { label: "Alertes de coordination", title: "Un problème clinique = une alerte, avec motif, heure et responsable.", points: ["Un même problème signalé plusieurs jours ne crée qu’une seule alerte", "L’escalade au médecin se fait en un clic et reste tracée", "La liste du jour dit ce qu’il reste à faire, par qui et pour quand"], cta: "Voir les alertes de coordination", visual: <AlertsVisual /> },
  { label: "Bilan avant chimiothérapie", title: "Le bilan arrive déposé, lu et vérifié, pas en pièce jointe.", points: ["Le patient ou l’infirmière dépose le bilan ; les valeurs sont lues puis vérifiées par l’IDEC", "Les valeurs hors norme sont signalées ; le médecin confirme ou reporte la cure", "Le secrétariat et le patient sont informés de la décision"], cta: "Voir le bilan pré-chimio", visual: <BilanVisual />, reverse: true },
  { label: "Traitement oral à domicile", title: "Les prises et les oublis sont visibles le jour même.", points: ["Rappel quotidien, prise cochée en un geste par le patient", "Les oublis remontent dans la liste du jour de l’infirmière", "Une pause ou un arrêt décidé par le médecin s’applique immédiatement chez le patient"], cta: "Voir le suivi du traitement oral", visual: <AdherenceVisual /> },
  { label: "Synthèse du parcours", title: "Deux pages qui résument le parcours, pour un confrère ou un autre établissement.", points: ["Traitements, dates clés, suivi des 90 derniers jours, bilans validés", "Générée en français ou en anglais par le médecin ou l’infirmière", "Numérotée, datée, signée : on sait qui l’a produite et quand"], cta: "Voir un exemple de synthèse", visual: <SummaryVisual />, reverse: true },
];

export function FeatureProofs() {
  return (
    <div className="space-y-20" data-testid="home-features-section">
      {FEATURES.map((f, i) => (
        <div key={f.label} className={`grid items-center gap-10 lg:grid-cols-2 ${f.reverse ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`home-feature-${i + 1}`}>
          <Reveal>
            <Label>{f.label}</Label>
            <h3 className="font-display text-2xl font-bold leading-tight text-slate-800 sm:text-3xl lg:text-4xl">{f.title}</h3>
            <ul className="mt-6 space-y-3">
              {f.points.map((p) => (
                <li key={p} className="flex items-start gap-3 text-base leading-7 text-slate-600"><ClipboardCheck className="mt-1 h-5 w-5 shrink-0 text-emerald-500" />{p}</li>
              ))}
            </ul>
            <Link to="/professionnels" className={`${ctaSecondary} mt-8`} data-testid={`home-feature-cta-${i + 1}`}>{f.cta} <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
          <Reveal delay={0.1} y={32}>{f.visual}</Reveal>
        </div>
      ))}
    </div>
  );
}

function Frame({ children, testId }) {
  return (
    <div className="rounded-[2rem] border border-emerald-100 bg-white p-4 shadow-[0_28px_90px_rgba(15,23,42,0.10)]" data-testid={testId}>
      <div className="rounded-[1.5rem] border border-slate-100 bg-slate-50/70 p-5">{children}</div>
    </div>
  );
}

function Row({ title, sub, tag, tone = "bg-emerald-50 text-emerald-700" }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-100 bg-white px-4 py-3">
      <div><p className="text-sm font-extrabold text-slate-800">{title}</p><p className="text-xs font-semibold text-slate-400">{sub}</p></div>
      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${tone}`}>{tag}</span>
    </div>
  );
}

function AlertsVisual() {
  return (
    <Frame testId="home-visual-alerts">
      <p className="mb-3 text-xs font-extrabold uppercase tracking-[0.18em] text-slate-400">À traiter aujourd'hui</p>
      <div className="space-y-2">
        <Row title="Nausées grade 3 · signalées 2 fois" sub="Digestif · aujourd’hui 08:15" tag="Critique" tone="bg-rose-50 text-rose-700" />
        <Row title="Dyspnée grade 2" sub="Respiratoire · hier 19:40" tag="À traiter" tone="bg-amber-50 text-amber-700" />
        <Row title="Bilan reçu · à vérifier" sub="Prélèvement du 12/09" tag="Labo" />
      </div>
    </Frame>
  );
}

function BilanVisual() {
  return (
    <Frame testId="home-visual-bilan">
      <p className="mb-3 text-xs font-extrabold uppercase tracking-[0.18em] text-slate-400">Bilan pré-chimio · J-1</p>
      <div className="grid grid-cols-3 gap-2">
        {[["PNN", "1,9 G/L", "ok"], ["Hb", "10,4 g/dL", "bas"], ["Plaquettes", "185 G/L", "ok"]].map(([k, v, s]) => (
          <div key={k} className="rounded-xl border border-slate-100 bg-white p-3"><p className="text-xs font-bold text-slate-400">{k}</p><p className={`text-lg font-extrabold ${s === "bas" ? "text-amber-600" : "text-slate-800"}`}>{v}</p></div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between rounded-xl bg-emerald-600 px-4 py-3 text-white"><p className="text-sm font-extrabold">Décision médecin</p><span className="rounded-full bg-white/20 px-3 py-1 text-xs font-bold">GO · Dr Tazi · 17:05</span></div>
    </Frame>
  );
}

function AdherenceVisual() {
  return (
    <Frame testId="home-visual-adherence">
      <p className="mb-3 text-xs font-extrabold uppercase tracking-[0.18em] text-slate-400">Capécitabine · 7 derniers jours</p>
      <div className="flex gap-2">
        {["L", "M", "M", "J", "V", "S", "D"].map((d, i) => (
          <div key={i} className={`flex-1 rounded-xl py-3 text-center text-xs font-extrabold ${i === 4 ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>{d}<br />{i === 4 ? "—" : "✓"}</div>
        ))}
      </div>
      <p className="mt-3 text-sm font-semibold text-slate-500">6 prises sur 7 · l’oubli de vendredi est remonté à l’infirmière</p>
    </Frame>
  );
}

function SummaryVisual() {
  return (
    <Frame testId="home-visual-summary">
      <div className="flex items-center justify-between"><p className="text-sm font-extrabold text-teal-700">Synthèse du parcours — HANA</p><span className="font-mono text-[10px] text-slate-400">HANA-SYN-20260914-52FED3</span></div>
      <div className="mt-3 space-y-2">
        {["1. Identité & pathologie", "2. Situation thérapeutique", "3. Chronologie", "4. Suivi 90 jours", "5. Biologie validée", "6. Photographie"].map((s) => (
          <div key={s} className="rounded-lg bg-teal-600/90 px-3 py-1.5 text-xs font-bold text-white">{s}</div>
        ))}
      </div>
      <p className="mt-3 text-xs font-semibold text-slate-400">2 pages · français ou anglais · données enregistrées, sans interprétation</p>
    </Frame>
  );
}

const FAQ = [
  ["HANA est-il un dossier patient informatisé ?", "Non. HANA structure ce qui se passe entre les venues et entre les acteurs. Votre dossier patient, votre agenda et vos prescriptions restent dans vos outils."],
  ["HANA est-il une application pour les patients ?", "Le patient dispose d’une application, mais HANA est d’abord une infrastructure de coordination : l’information qu’il transmet rejoint l’IDEC, puis l’équipe médicale, avec traçabilité."],
  ["HANA prend-il des décisions médicales ?", "Non. HANA classe et priorise selon vos règles ; une infirmière vérifie, le médecin décide. Toujours."],
  ["Qui voit les données du patient ?", "Uniquement l’équipe qui le suit, selon son rôle. Tout usage secondaire passe par une pseudonymisation forte avec techniques d’anonymisation et par le consentement du patient."],
  ["HANA est-il réservé à l’oncologie ?", "C’est notre parcours de départ et notre preuve d’exécution. L’architecture est prévue pour d’autres parcours longs ; nous les ouvrirons avec les équipes concernées."],
  ["Combien de temps pour démarrer ?", "Quelques jours : création de l’établissement et des comptes, import du planning de cures et des protocoles. Aucun branchement informatique n’est nécessaire pour commencer."],
];

export function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="scroll-mt-20 px-5 py-20 lg:px-8 lg:py-24" data-testid="home-faq-section">
      <div className="mx-auto max-w-4xl">
        <Reveal><Label>Questions fréquentes</Label>
          <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-slate-800 sm:text-4xl" data-testid="home-faq-title">Les questions que les équipes posent en premier.</h2>
        </Reveal>
        <div className="mt-10 divide-y divide-emerald-100 rounded-[1.75rem] border border-emerald-100 bg-white" data-testid="home-faq-list">
          {FAQ.map(([q, a], i) => (
            <div key={q}>
              <button type="button" onClick={() => setOpen(open === i ? -1 : i)} className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left" data-testid={`home-faq-toggle-${i + 1}`} aria-expanded={open === i}>
                <span className="text-base font-extrabold text-slate-800">{q}</span>
                <motion.span animate={{ rotate: open === i ? 180 : 0 }} transition={{ duration: 0.25 }}><ChevronDown className="h-5 w-5 text-teal-500" /></motion.span>
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: "easeOut" }} className="overflow-hidden">
                    <p className="px-6 pb-6 text-base leading-7 text-slate-500" data-testid={`home-faq-answer-${i + 1}`}>{a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PatientTeaser() {
  return (
    <section id="patients" className="scroll-mt-20 border-y border-emerald-100 bg-white px-5 py-20 lg:px-8" data-testid="home-patient-section">
      <div className="mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-[1fr_0.9fr]">
        <Reveal>
          <Label>Vous êtes patient ?</Label>
          <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-slate-800 sm:text-4xl" data-testid="home-patient-title">Votre parcours continue chez vous. Votre équipe reste à vos côtés.</h2>
          <p className="mt-5 text-lg leading-8 text-slate-500" data-testid="home-patient-text">Depuis votre téléphone : signalez vos symptômes, suivez votre traitement, envoyez vos examens, retrouvez vos rendez-vous. Votre infirmière voit ce qui change et prévient le médecin si nécessaire.</p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link to="/login" className={ctaPrimary} data-testid="home-patient-login-button">Accéder à mon espace <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/patients" className={ctaSecondary} data-testid="home-patient-activation-button">HANA pour les patients</Link>
          </div>
        </Reveal>
        <Stagger className="grid gap-4 sm:grid-cols-3" testId="home-patient-cards">
          {[[Smartphone, "Votre téléphone", "Symptômes, traitement, examens, rendez-vous : tout au même endroit"], [Users, "Votre équipe", "Votre infirmière voit ce qui change et prévient le médecin si besoin"], [ShieldCheck, "Vos données", "Vues uniquement par l’équipe qui vous soigne"]].map(([Icon, k, v]) => (
            <StaggerItem key={k} className="rounded-2xl border border-emerald-100 bg-slate-50 p-5" testId={`home-patient-card-${k.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-")}`}>
              <Icon className="h-5 w-5 text-teal-500" /><p className="mt-3 text-xl font-extrabold text-emerald-600">{k}</p><p className="mt-1 text-sm font-semibold text-slate-500">{v}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

export function FinalCta() {
  return (
    <section id="demo" className="scroll-mt-20 px-5 py-20 lg:px-8" data-testid="public-final-cta-section">
      <Reveal y={32}>
        <div className="mx-auto max-w-7xl rounded-[2rem] bg-emerald-600 p-8 text-white shadow-[0_28px_80px_rgba(5,150,105,0.18)] md:p-12">
          <div className="grid gap-8 lg:grid-cols-[1fr_0.75fr] lg:items-center">
            <div data-testid="final-cta-copy">
              <Label light>Pour votre établissement</Label>
              <h2 className="font-display text-3xl font-bold leading-tight sm:text-4xl" data-testid="final-cta-title">Voyez HANA sur les parcours de votre établissement.</h2>
              <p className="mt-5 text-lg leading-8 text-emerald-50" data-testid="final-cta-description">Une démonstration de trente minutes avec votre planning de cures, vos protocoles et vos règles d’alerte. Sans engagement.</p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row lg:justify-end" data-testid="final-cta-buttons">
              <Link to={DEMO_PATH} className={`${cta} bg-white text-emerald-700 hover:bg-emerald-50`} data-testid="final-cta-demo-button">Demander une démonstration <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/securite" className={`${cta} border border-white/40 text-white hover:bg-white/10`} data-testid="final-cta-security-button">Sécurité et données</Link>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
